import { Controller } from '@hotwired/stimulus'
import DeviceMobileMixin from './mixins/device_mobile_mixin.js'

export default class BiometricAuthSetupController extends Controller {
  static targets = [
    'modal',
    'section',
    'title',
    'separator',
    'biometricMessage',
    'faceIdMessage',
    'touchIdMessage',
    'revokeButton',
    'acceptButton'
  ];
  static values = {
    'createOauthTokensUrl': String,
    'getDevicesUrl': String,
    'createDeviceUrl': String,
  };
  static DEBUG_MODE = true;

  async connect() {
    this._debug('BiometricAuthSetupController connected');
    const onMobile = await this._onMobile();
    if (!onMobile) return;
    const biometricAvailability = await this._checkBiometricAvailability();
    if (biometricAvailability && biometricAvailability.available) {
      this._debug('Biometric authentication is available');
      this._revealSection();
      if (biometricAvailability.alreadySetup) {
        this._debug('Biometric authentication is already set up');
        if (this.hasRevokeButtonTarget) {
          this.revokeButtonTarget.classList.remove('d-none');
        }
      } else if (this.hasAcceptButtonTarget) {
        this.acceptButtonTarget.classList.remove('d-none');
      }

      if (biometricAvailability.type === 'faceId') {
        this.faceIdMessageTarget.classList.remove('d-none');
      } else if (biometricAvailability.type === 'touchId') {
        this.touchIdMessageTarget.classList.remove('d-none');
      } else {
        this.biometricMessageTarget.classList.remove('d-none');
      }

      const alreadyAcceptedOrRefused = await this._alreadyAcceptedOrRefused('biometric');
      this._debug('alreadyAcceptedOrRefused: ' + alreadyAcceptedOrRefused);

      if (!alreadyAcceptedOrRefused && this.hasModalTarget) {
        this.promptModal = new bootstrap.Modal(this.modalTarget, {
          backdrop: 'static',
          keyboard: false
        });
        this._askForBiometricAuth();
      }
    } else {
      this._debug('Biometric authentication is not available');
    }
  }

  _revealSection() {
    if (this.hasSectionTarget) {
      this.sectionTarget.classList.remove('d-none');
    }
    if (this.hasTitleTarget) {
      this.titleTarget.classList.remove('d-none');
    }
    if (this.hasSeparatorTarget) {
      this.separatorTarget.classList.remove('d-none');
    }
  }

  async accept() {
    this._debug('User accepted biometric authentication setup');
    const secret = await this._getOauthTokensForCurrentUser();
    if (!secret) return;
    await Median.auth.save({secret: JSON.stringify(secret)});
    await this._setupBiometric(true);

    if (this.hasModalTarget) {
      this.promptModal.hide();
    }
    if (this.hasAcceptButtonTarget) {
      this.acceptButtonTarget.classList.add('d-none');
    }
    if (this.hasRevokeButtonTarget) {
      this.revokeButtonTarget.classList.remove('d-none');
    }
  }

  async later() {
    this._debug('User chose to set up biometric authentication later');
    try {
      await Median.auth.delete();
    } catch (error) {
      this._debug('Error deleting biometric authentication: ' + JSON.stringify(error));
    }
    await this._setupBiometric(false);

    if (this.hasAcceptButtonTarget) {
      this.acceptButtonTarget.classList.remove('d-none');
    }
    if (this.hasRevokeButtonTarget) {
      this.revokeButtonTarget.classList.add('d-none');
    }
    if (this.hasModalTarget) {
      this.promptModal.hide();
    }
  }

  async _setupBiometric(accept) {
    const info = await this._getDeviceInfo();
    const params = {
      installation_id: info?.installationId,
      device_name: info?.deviceName,
    };
    if (accept) {
      params['biometric_accepted_at'] = new Date();
      params['biometric_refused_at'] = '';
    } else {
      params['biometric_refused_at'] = new Date();
      params['biometric_accepted_at'] = '';
    }
    await this._registerDevice(params);
  }

  async _checkBiometricAvailability() {
    try {
      const result = await Median.auth.status();
      this._debug('Biometric availability result: ' + JSON.stringify(result));
      if (result) {
        return {
          available: result.hasTouchId,
          type: result.biometryType, // 'touchId', 'faceId', or 'none'
          alreadySetup: result.hasSecret,
        };
      }
    } catch (error) {
      this._debug('Error checking biometric availability: ' + error);
    }
    return false;
  }

  _askForBiometricAuth() {
    this.promptModal.show();
  }

  async _getOauthTokensForCurrentUser() {
    try {
      const response = await fetch(this.createOauthTokensUrlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        this._debug('Successfully obtained oauth tokens: ' + JSON.stringify(data));
        return {
          access_token: data?.access_token,
          refresh_token: data?.refresh_token,
          client_uid: data?.client_uid,
        };
      } else {
        this._debug('Failed to get oauth tokens, status: ' + response.status);
        return null;
      }
    } catch (error) {
      this._debug('Error getting oauth tokens: ' + error);
      return null;
    }
  }
}

Object.assign(BiometricAuthSetupController.prototype, DeviceMobileMixin);
